import { useRef, useEffect, useCallback } from 'react';
import { Bold, Italic, Heading2, Heading3, List, ListOrdered, Quote, Link2, Highlighter, Undo2 } from 'lucide-react';

/**
 * Lightweight WYSIWYG editor that outputs clean HTML.
 * Uses the (still broadly supported) execCommand API — no heavy deps.
 * Controlled by `value` (HTML string); emits HTML via `onChange`.
 */
const RichTextEditor = ({ value, onChange, placeholder = 'Write the reading content…', testid = 'rich-editor', invalid = false }) => {
  const ref = useRef(null);

  // Sync external value in only when the editor is NOT focused (avoids caret jumps while typing).
  useEffect(() => {
    const el = ref.current;
    if (el && document.activeElement !== el && (value || '') !== el.innerHTML) {
      el.innerHTML = value || '';
    }
  }, [value]);

  const emit = useCallback(() => {
    if (!ref.current) return;
    // Normalize the WYSIWYG's whitespace pollution so the stored HTML
    // matches the tight `.prose-reading` rhythm used everywhere the
    // content is rendered (learning module, event detail, discover, etc.).
    // Chrome/Safari sprinkle `<p><br></p>` / `<div><br></div>` when the user
    // presses Enter after a heading, which then adds ~1.8em of dead space.
    // Firefox sometimes emits stray `<br>` between block-level nodes.
    const raw = ref.current.innerHTML;
    const cleaned = (raw || '')
      .replace(/<p[^>]*>\s*(<br\s*\/?>|&nbsp;|\u00a0)?\s*<\/p>/gi, '')
      .replace(/<div[^>]*>\s*(<br\s*\/?>|&nbsp;|\u00a0)?\s*<\/div>/gi, '')
      // Two-or-more consecutive <br>s → single <br>. execCommand-style
      // "shift+enter twice" was the other source of double-height gaps.
      .replace(/(<br\s*\/?>\s*){2,}/gi, '<br>');
    onChange(cleaned);
  }, [onChange]);

  // Strip foreign text/background colors from pasted content so it always adopts
  // the portal theme (fixes pasted essays showing dark text in dark mode). Our own
  // highlight tool re-applies a background afterwards if the author wants one.
  const cleanCss = (css) => css.split(';').filter((d) => {
    const p = (d.split(':')[0] || '').trim().toLowerCase();
    return p && p !== 'color' && p !== 'background' && p !== 'background-color';
  }).join(';');
  const cleanPastedHtml = (html) => (html || '')
    .replace(/<\/?(meta|style|script|o:p|xml)[^>]*>/gi, '')
    .replace(/<font([^>]*)>/gi, (m, a) => `<font${a.replace(/\s(?:color|bgcolor)\s*=\s*("[^"]*"|'[^']*'|[^\s>]+)/gi, '')}>`)
    .replace(/style\s*=\s*"([^"]*)"/gi, (m, css) => { const c = cleanCss(css); return c ? `style="${c}"` : ''; })
    .replace(/style\s*=\s*'([^']*)'/gi, (m, css) => { const c = cleanCss(css); return c ? `style='${c}'` : ''; });

  const onPaste = (e) => {
    e.preventDefault();
    const cb = e.clipboardData || window.clipboardData;
    const html = cb && cb.getData('text/html');
    const text = cb && cb.getData('text/plain');
    if (html) document.execCommand('insertHTML', false, cleanPastedHtml(html));
    else if (text) document.execCommand('insertText', false, text);
    emit();
  };

  const exec = (command, arg = null) => {
    ref.current?.focus();
    document.execCommand(command, false, arg);
    emit();
  };

  const addLink = () => {
    const url = window.prompt('Link URL', 'https://');
    if (url) exec('createLink', url);
  };

  const TOOLS = [
    { icon: Bold, cmd: () => exec('bold'), label: 'Bold', key: 'bold' },
    { icon: Italic, cmd: () => exec('italic'), label: 'Italic', key: 'italic' },
    { icon: Heading2, cmd: () => exec('formatBlock', 'H2'), label: 'Heading', key: 'h2' },
    { icon: Heading3, cmd: () => exec('formatBlock', 'H3'), label: 'Subheading', key: 'h3' },
    { icon: List, cmd: () => exec('insertUnorderedList'), label: 'Bullet list', key: 'ul' },
    { icon: ListOrdered, cmd: () => exec('insertOrderedList'), label: 'Numbered list', key: 'ol' },
    { icon: Quote, cmd: () => exec('formatBlock', 'BLOCKQUOTE'), label: 'Callout / quote', key: 'quote' },
    { icon: Highlighter, cmd: () => { ref.current?.focus(); document.execCommand('hiliteColor', false, '#fde68a') || document.execCommand('backColor', false, '#fde68a'); emit(); }, label: 'Highlight', key: 'highlight' },
    { icon: Link2, cmd: addLink, label: 'Link', key: 'link' },
    { icon: Undo2, cmd: () => exec('undo'), label: 'Undo', key: 'undo' },
  ];

  return (
    <div className={`rounded-xl border ${invalid ? 'border-destructive ring-1 ring-destructive/40' : 'border-border'} bg-background overflow-hidden transition-colors`} data-testid={testid}>
      <div className={`flex flex-wrap items-center gap-0.5 border-b ${invalid ? 'border-destructive/40' : 'border-border'} bg-secondary/40 px-1.5 py-1`}>
        {TOOLS.map((t) => (
          <button
            key={t.key}
            type="button"
            onMouseDown={(e) => { e.preventDefault(); t.cmd(); }}
            title={t.label}
            data-testid={`${testid}-${t.key}`}
            className="h-8 w-8 grid place-items-center rounded-md text-muted-foreground hover:text-foreground hover:bg-background transition-colors"
          >
            <t.icon className="h-4 w-4" />
          </button>
        ))}
      </div>
      <div
        ref={ref}
        contentEditable
        suppressContentEditableWarning
        onInput={emit}
        onBlur={emit}
        onPaste={onPaste}
        data-placeholder={placeholder}
        data-testid={`${testid}-area`}
        className="rich-editor-area min-h-[180px] max-h-[360px] overflow-y-auto px-4 py-3 text-sm leading-relaxed focus:outline-none prose-reading"
      />
    </div>
  );
};

export default RichTextEditor;
